import { actionTypes, PORT_HUB_TYPES } from '../actions/data';

const blankSectionState = () => ({
  status: 'idle',
  data: [],
  error: null,
});

const buildInitialSectionsState = () => {
  return PORT_HUB_TYPES.reduce((acc, type) => {
    acc[type] = blankSectionState();
    return acc;
  }, {});
};

const summarizeSections = (sections) => {
  const trackedTypes = PORT_HUB_TYPES;
  let aggregated = [];
  let successCount = 0;
  let errorCount = 0;
  let emptySuccessCount = 0;
  let hasAnyCompletion = false;

  trackedTypes.forEach((type) => {
    const section = sections[type] || blankSectionState();
    if (section.status === 'success') {
      successCount += 1;
      hasAnyCompletion = true;
      const data = Array.isArray(section.data) ? section.data : [];
      if (data.length === 0) {
        emptySuccessCount += 1;
      }
      aggregated = aggregated.concat(data);
    } else if (section.status === 'error') {
      errorCount += 1;
      hasAnyCompletion = true;
    }
  });

  // Include any additional sections (if they ever appear) in the aggregate so the UI stays in sync
  Object.keys(sections).forEach((key) => {
    if (trackedTypes.indexOf(key) !== -1) return;
    const section = sections[key];
    if (section && section.status === 'success' && Array.isArray(section.data)) {
      aggregated = aggregated.concat(section.data);
      hasAnyCompletion = true;
    }
  });

  const totalTracked = trackedTypes.length;
  const resolvedCount = successCount + errorCount;
  const pendingCount = Math.max(0, totalTracked - resolvedCount);
  const allResolved = resolvedCount === totalTracked;
  const allErrors = allResolved && errorCount === totalTracked;
  const allSuccessEmpty =
    allResolved && successCount === totalTracked && emptySuccessCount === totalTracked;

  const sortedAggregate =
    aggregated.length > 1
      ? [...aggregated].sort((a, b) =>
          new Date(a.updateDate) >= new Date(b.updateDate) ? -1 : 1
        )
      : aggregated;

  return {
    aggregated: sortedAggregate,
    successCount,
    errorCount,
    pendingCount,
    totalTracked,
    resolvedCount,
    hasAnyCompletion,
    allResolved,
    allErrors,
    allSuccessEmpty,
  };
};

const createSummaryState = () => summarizeSections(buildInitialSectionsState());

const applySectionUpdate = (state, portHubType, updates) => {
  if (!portHubType) {
    return state;
  }

  const nextSections = {
    ...state.personalStrategiesSections,
    [portHubType]: {
      ...(state.personalStrategiesSections?.[portHubType] || blankSectionState()),
      ...updates,
    },
  };

  const summary = summarizeSections(nextSections);

  return {
    ...state,
    personalStrategies: summary.hasAnyCompletion
      ? summary.aggregated
      : state.personalStrategies,
    personalStrategiesSections: nextSections,
    sectionsSummary: summary,
    strategiesLoadError: summary.allErrors,
  };
};

// DEFAULT STATE
export const initData = {
  personalStrategies: null,
  strategyIdToShow: null,
  strategiesLoadError: false,
  personalStrategiesSections: buildInitialSectionsState(),
  sectionsSummary: createSummaryState(),
};

// WRITES
const data = (state = {}, action) => {
  switch (action.type) {
    case actionTypes.LOAD_PERSONAL_STRATEGIES:
      return Object.assign({}, state, {
        personalStrategies: action.value,
      });

    case actionTypes.LOAD_PERSONAL_STRATEGIES_ERROR:
      return {
        ...state,
        personalStrategies: [],
        strategiesLoadError: true,
      };

    case actionTypes.RESET_PERSONAL_STRATEGIES_STATE: {
      const sections = buildInitialSectionsState();
      return {
        ...state,
        personalStrategies: null,
        strategiesLoadError: false,
        personalStrategiesSections: sections,
        sectionsSummary: summarizeSections(sections),
      };
    }

    case actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_START:
      return applySectionUpdate(state, action.portHubType, {
        status: 'loading',
        error: null,
        data: [],
      });

    case actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_SUCCESS:
      return applySectionUpdate(state, action.portHubType, {
        status: 'success',
        error: null,
        data: action.value || [],
      });

    case actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_ERROR:
      return applySectionUpdate(state, action.portHubType, {
        status: 'error',
        error: action.error || 'Error loading portfolios.',
        data: [],
      });

    case actionTypes.LOAD_BENCHMARKS:
      return Object.assign({}, state, {
        benchmarks: action.value.benchmarks,
      });

    case actionTypes.DELETE_A_STRATEGY:
      return {
        ...state,
        personalStrategies: state.personalStrategies.filter(
          (d) => d.id !== action.value
        ),
      };

    case actionTypes.SHOW_A_STRATEGY:
      const strategy = state.personalStrategies.filter(
        (d) => d.id === action.value
      );

      // If the new stategy exists then show that, otherwise keep it as is
      return {
        ...state,
        strategyIdToShow: strategy && strategy.length > 0 ? action.value : null,
      };

    case actionTypes.SHOW_ALL_STRATEGIES:
      return {
        ...state,
        strategyIdToShow: null,
      };

    case actionTypes.SHOW_STRATEGY_EDITOR:
      return {
        ...state,
        strategyToEdit: action.value,
        strategyEditorVisible: true,
      };

    case actionTypes.CLOSE_STRATEGY_EDITOR:
      return {
        ...state,
        strategyToEdit: null,
        strategyEditorVisible: false,
      };

    case actionTypes.UPDATE_STRATEGY:
      // Filter out the stategy that was updated
      const remainingStrategies = state.personalStrategies.filter(
        (d) => d.id !== action.value.id
      );

      // Update the array of all strategies
      const updatedStrategies = [action.value].concat(remainingStrategies);

      return {
        ...state,
        personalStrategies: updatedStrategies,
      };

    case actionTypes.SAVING_NEW_STRATEGY_IN_PROGRESS:
      // Update the array of all strategies
      const newStrategy = {
        id: 'newStrategy',
        name: action.value.name,
      };

      return {
        ...state,
        strategySaveInProgress: 'newStrategy', // Placeholder ID for new strategy
        personalStrategies: [newStrategy].concat(state.personalStrategies),
      };

    case actionTypes.SAVING_STRATEGY_IN_PROGRESS:
      return {
        ...state,
        strategySaveInProgress: action.value, // ID of the Strategy being saved
      };

    case actionTypes.SAVING_STRATEGY_COMPLETE:
      return {
        ...state,
        strategySaveInProgress: null, // ID of the Strategy being saved
      };

    case actionTypes.SAVE_NEW_STRATEGY:
      // Create new array of strategies, filtering out the temporary one that was
      // in place whilst updating
      const newStrategies = [action.value].concat(
        state.personalStrategies.filter(
          (perStrat) => perStrat.id !== 'newStrategy'
        )
      );

      return {
        ...state,
        personalStrategies: newStrategies,
      };

    default:
      return state;
  }
};

export default data;
